/**
 * PIPELINE BUILDER
 * 
 * Visual pipeline creation interface.
 * Drag-and-drop style agent orchestration with live execution view.
 * 
 * Think: Node-based editor meets mission control
 */

import React, { useState, useEffect } from 'react';

// Agent Node Component
const AgentNode = ({ agent, position, isActive, isCompleted, onClick, onConnect }) => {
  const agentStyles = {
    content_engine: {
      gradient: 'from-purple-500 to-blue-600',
      icon: '🤖',
      glow: 'shadow-purple-500/30'
    },
    competitor_spy: {
      gradient: 'from-amber-500 to-orange-600',
      icon: '🦉',
      glow: 'shadow-amber-500/30'
    },
    distribution_machine: {
      gradient: 'from-emerald-500 to-cyan-600',
      icon: '🚀',
      glow: 'shadow-emerald-500/30'
    }
  };
  
  const style = agentStyles[agent.type] || agentStyles.content_engine;
  
  return (
    <div 
      className={`
        relative w-48 p-4 rounded-xl cursor-pointer
        bg-gray-900 border-2 transition-all duration-300
        ${isActive ? `border-cyan-400 ${style.glow} shadow-lg` :
          isCompleted ? 'border-emerald-500' :
          'border-gray-700 hover:border-gray-600'}
      `}
      style={{ position: 'absolute', left: position.x, top: position.y }}
      onClick={onClick}
    >
      {/* Status indicator */}
      {isActive && (
        <div className="absolute -top-1 -right-1 w-3 h-3 bg-cyan-400 rounded-full animate-pulse" />
      )}
      {isCompleted && (
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center text-xs">
          ✓
        </div>
      )}
      
      {/* Agent content */}
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${style.gradient} flex items-center justify-center text-xl`}>
          {style.icon}
        </div>
        <div>
          <h4 className="font-semibold text-white text-sm">{agent.name}</h4>
          <p className="text-xs text-gray-500">{agent.type.replace('_', ' ')}</p>
        </div>
      </div>
      
      {/* Connection ports */}
      <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-gray-700 rounded-full border-2 border-gray-600 hover:border-cyan-400 transition-colors" />
      <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-gray-700 rounded-full border-2 border-gray-600 hover:border-cyan-400 transition-colors" />
    </div>
  );
};

// Connection Line Component
const ConnectionLine = ({ from, to, isActive }) => {
  // Calculate path between nodes
  const startX = from.x + 192; // Node width
  const startY = from.y + 40; // Half node height
  const endX = to.x;
  const endY = to.y + 40;
  
  const midX = (startX + endX) / 2;
  
  const path = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;
  
  return (
    <svg className="absolute inset-0 pointer-events-none" style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#06b6d4" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </linearGradient>
      </defs>
      
      {/* Background path */}
      <path
        d={path}
        fill="none"
        stroke="#374151"
        strokeWidth="2"
      />
      
      {/* Active path with animation */}
      {isActive && (
        <>
          <path
            d={path}
            fill="none"
            stroke="url(#lineGradient)"
            strokeWidth="2"
            strokeDasharray="5 5"
            className="animate-dash"
          />
          {/* Flowing dot */}
          <circle r="4" fill="#06b6d4">
            <animateMotion dur="1.5s" repeatCount="indefinite" path={path} />
          </circle>
        </>
      )}
      
      <style>{`
        @keyframes dash {
          to { stroke-dashoffset: -20; }
        }
        .animate-dash {
          animation: dash 1s linear infinite;
        }
      `}</style>
    </svg>
  );
};

// Pipeline Template Card
const PipelineTemplateCard = ({ template, onSelect }) => {
  // Handle both API format (steps_preview) and local format (agents)
  const agents = template.steps_preview || template.agents || [];
  
  return (
    <div 
      className="
        p-5 rounded-xl border-2 border-gray-800 bg-gray-900/50
        hover:border-cyan-500/50 hover:bg-gray-900 cursor-pointer
        transition-all duration-300 group
      "
      onClick={() => onSelect(template)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{template.emoji || template.icon || '⚡'}</span>
          <div>
            <h4 className="font-semibold text-white group-hover:text-cyan-400 transition-colors">
              {template.name}
            </h4>
            <p className="text-xs text-gray-500">{agents.length} agents</p>
          </div>
        </div>
        <span className="text-xs text-gray-600 group-hover:text-cyan-400 transition-colors">→</span>
      </div>
      
      <p className="text-sm text-gray-400 mb-4">{template.tagline || template.description}</p>
      
      {/* Agent flow preview */}
      <div className="flex items-center gap-2">
        {agents.map((agent, i) => (
          <React.Fragment key={i}>
            <div className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center text-sm">
              {agent.emoji || agent.icon || '🤖'}
            </div>
            {i < agents.length - 1 && (
              <div className="text-gray-600">→</div>
            )}
          </React.Fragment>
        ))}
      </div>
      
      <div className="mt-4 pt-3 border-t border-gray-800 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          ~{template.credits_estimate || 30} credits/run
        </span>
        <span className="text-xs text-amber-400">
          {template.time_saved || 'Saves time'}
        </span>
      </div>
    </div>
  );
};

// Execution Progress Panel
const ExecutionProgress = ({ pipeline, currentStep, logs }) => {
  return (
    <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-800 bg-gray-800/50">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-white">Execution Progress</h3>
          <span className={`
            px-2 py-1 rounded text-xs font-medium
            ${currentStep === -1 ? 'bg-emerald-500/20 text-emerald-400' :
              currentStep >= 0 ? 'bg-cyan-500/20 text-cyan-400' :
              'bg-gray-700 text-gray-400'}
          `}>
            {currentStep === -1 ? 'Completed' :
             currentStep >= 0 ? 'Running' : 'Idle'}
          </span>
        </div>
      </div>
      
      {/* Steps */}
      <div className="p-4 space-y-3">
        {pipeline.steps.map((step, i) => (
          <div 
            key={i}
            className={`
              flex items-center gap-3 p-3 rounded-lg
              ${i === currentStep ? 'bg-cyan-500/10 border border-cyan-500/30' :
                i < currentStep ? 'bg-emerald-500/10' :
                'bg-gray-800/50'}
            `}
          >
            <div className={`
              w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
              ${i === currentStep ? 'bg-cyan-500 text-white animate-pulse' :
                i < currentStep ? 'bg-emerald-500 text-white' :
                'bg-gray-700 text-gray-400'}
            `}>
              {i < currentStep ? '✓' : i + 1}
            </div>
            <div className="flex-1">
              <p className="font-medium text-white">{step.name}</p>
              <p className="text-xs text-gray-500">{step.agent_type}</p>
            </div>
            {i === currentStep && (
              <div className="flex items-center gap-2 text-cyan-400">
                <span className="w-2 h-2 bg-cyan-400 rounded-full animate-ping" />
                <span className="text-xs">Processing...</span>
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Logs */}
      {logs.length > 0 && (
        <div className="border-t border-gray-800">
          <div className="px-4 py-2 bg-gray-800/30">
            <p className="text-xs text-gray-500">EXECUTION LOG</p>
          </div>
          <div className="p-4 max-h-40 overflow-y-auto font-mono text-xs space-y-1">
            {logs.map((log, i) => (
              <div key={i} className={`
                ${log.type === 'error' ? 'text-red-400' :
                  log.type === 'success' ? 'text-emerald-400' :
                  'text-gray-400'}
              `}>
                <span className="text-gray-600">[{log.time}]</span> {log.message}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Pipeline Builder Component
const PipelineBuilder = ({ API }) => {
  const [view, setView] = useState('templates'); // templates, builder, execution
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [userPipelines, setUserPipelines] = useState([]);
  const [currentPipeline, setCurrentPipeline] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [currentStep, setCurrentStep] = useState(-2); // -2 = idle, -1 = complete, 0+ = step index
  const [executionLogs, setExecutionLogs] = useState([]);
  
  // Fetch templates and user pipelines
  useEffect(() => {
    let isMounted = true;
    
    const fetchTemplates = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API}/pipelines/templates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok && isMounted) {
          const data = await response.json();
          setTemplates(data.templates || []);
        }
      } catch (err) {
        console.error('Failed to fetch templates:', err);
        // Use default templates if API fails
        if (isMounted) {
          setTemplates([
            {
              id: 'spy-and-create',
              name: 'Spy & Create',
              icon: '🎯',
              description: 'Athena discovers gaps, then Hermes forges content to fill them.',
              agents: [
                { type: 'competitor_spy', icon: '🦉', name: 'Athena' },
                { type: 'content_engine', icon: '👟', name: 'Hermes' }
              ],
              credits_estimate: 45,
              time_saved: '15+ hours'
            },
            {
              id: 'full-funnel',
              name: 'Full Funnel',
              icon: '🚀',
              description: 'Complete pipeline: Research → Create → Distribute',
              agents: [
                { type: 'competitor_spy', icon: '🦉', name: 'Athena' },
                { type: 'content_engine', icon: '👟', name: 'Hermes' },
                { type: 'distribution_machine', icon: '🚀', name: 'Distributor' }
              ],
              credits_estimate: 65,
              time_saved: '25+ hours'
            },
            {
              id: 'content-blitz',
              name: 'Content Blitz',
              icon: '⚡',
              description: 'Run Hermes multiple times for a content forge burst.',
              agents: [
                { type: 'content_engine', icon: '👟', name: 'Hermes #1' },
                { type: 'content_engine', icon: '👟', name: 'Hermes #2' },
                { type: 'content_engine', icon: '👟', name: 'Hermes #3' }
              ],
              credits_estimate: 85,
              time_saved: '30+ hours'
            }
          ]);
        }
      }
    };
    
    const fetchUserPipelines = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API}/pipelines`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok && isMounted) {
          const data = await response.json();
          setUserPipelines(data.pipelines || []);
        }
      } catch (err) {
        console.error('Failed to fetch pipelines:', err);
      }
    };
    
    fetchTemplates();
    fetchUserPipelines();
    
    return () => { isMounted = false; };
  }, [API]);
  
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    
    // Handle both API format (steps_preview) and local format (agents)
    const agents = template.steps_preview || template.agents || [];
    
    // Create pipeline structure with positions
    const positions = agents.map((_, i) => ({
      x: 100 + (i * 250),
      y: 150
    }));
    
    setCurrentPipeline({
      name: template.name,
      template_id: template.template_id || template.id,
      steps: agents.map((agent, i) => ({
        name: agent.role || agent.name || agent.agent || 'Step',
        type: agent.agent || agent.type || 'content_engine',
        icon: agent.emoji || agent.icon || '🤖',
        position: positions[i]
      }))
    });
    
    setView('builder');
  };
  
  const handleCreatePipeline = async () => {
    if (!currentPipeline) return;
    
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/pipelines`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          template_id: currentPipeline.template_id,
          name: currentPipeline.name,
          configuration: {}
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentPipeline(prev => ({ ...prev, id: data.pipeline.id }));
        // Refresh pipelines list
        try {
          const pipelinesResponse = await fetch(`${API}/pipelines`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (pipelinesResponse.ok) {
            const pipelinesData = await pipelinesResponse.json();
            setUserPipelines(pipelinesData.pipelines || []);
          }
        } catch (refreshErr) {
          console.error('Failed to refresh pipelines:', refreshErr);
        }
      }
    } catch (err) {
      console.error('Failed to create pipeline:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRunPipeline = async () => {
    if (!currentPipeline?.id) {
      // Create first if not saved
      await handleCreatePipeline();
    }
    
    setIsExecuting(true);
    setCurrentStep(0);
    setExecutionLogs([]);
    setView('execution');
    
    const addLog = (message, type = 'info') => {
      const time = new Date().toLocaleTimeString();
      setExecutionLogs(prev => [...prev, { time, message, type }]);
    };
    
    try {
      const token = localStorage.getItem('token');
      
      // Simulate step-by-step execution
      for (let i = 0; i < currentPipeline.steps.length; i++) {
        setCurrentStep(i);
        addLog(`Starting ${currentPipeline.steps[i].name}...`);
        
        // Simulate execution time
        await new Promise(r => setTimeout(r, 3000));
        
        addLog(`${currentPipeline.steps[i].name} completed successfully`, 'success');
      }
      
      setCurrentStep(-1); // Completed
      addLog('Pipeline execution completed!', 'success');
      
    } catch (err) {
      addLog(`Error: ${err.message}`, 'error');
    } finally {
      setIsExecuting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center shadow-lg shadow-cyan-500/25">
                <span className="text-2xl">⚡</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
                  Pipeline Builder
                </h1>
                <p className="text-sm text-gray-500">Visual Agent Orchestration</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {view !== 'templates' && (
                <button
                  onClick={() => setView('templates')}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  ← Templates
                </button>
              )}
              
              {view === 'builder' && currentPipeline && (
                <button
                  onClick={handleRunPipeline}
                  disabled={isLoading || isExecuting}
                  className="
                    px-5 py-2.5 rounded-xl font-semibold
                    bg-gradient-to-r from-emerald-500 to-cyan-600
                    hover:from-emerald-400 hover:to-cyan-500
                    shadow-lg shadow-emerald-500/25
                    disabled:opacity-50
                    transition-all duration-300
                  "
                >
                  {isLoading ? '⏳ Saving...' : '▶️ Run Pipeline'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Templates View */}
        {view === 'templates' && (
          <div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Pipeline Templates</h2>
              <p className="text-gray-400">Start with a pre-built workflow or create your own</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {templates.map(template => (
                <PipelineTemplateCard
                  key={template.id}
                  template={template}
                  onSelect={handleSelectTemplate}
                />
              ))}
              
              {/* Custom Pipeline Card */}
              <div className="
                p-5 rounded-xl border-2 border-dashed border-gray-700
                hover:border-cyan-500/50 cursor-pointer
                transition-all duration-300 flex flex-col items-center justify-center
                min-h-[200px] group
              ">
                <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">+</div>
                <p className="font-semibold text-gray-400 group-hover:text-cyan-400">Create Custom Pipeline</p>
                <p className="text-xs text-gray-600 mt-1">Coming Soon</p>
              </div>
            </div>
            
            {/* User's Pipelines */}
            {userPipelines.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-white mb-4">Your Pipelines</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {userPipelines.map(pipeline => (
                    <div 
                      key={pipeline.id}
                      className="p-4 rounded-xl bg-gray-900 border border-gray-800 hover:border-gray-700 cursor-pointer transition-colors"
                      onClick={() => {
                        setCurrentPipeline(pipeline);
                        setView('builder');
                      }}
                    >
                      <h4 className="font-semibold text-white">{pipeline.name}</h4>
                      <p className="text-xs text-gray-500">{pipeline.steps?.length || 0} steps</p>
                      <p className="text-xs text-gray-600 mt-2">Last run: {pipeline.last_run || 'Never'}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Builder View */}
        {view === 'builder' && currentPipeline && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Canvas */}
            <div className="lg:col-span-2">
              <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <input
                    type="text"
                    value={currentPipeline.name}
                    onChange={(e) => setCurrentPipeline(prev => ({ ...prev, name: e.target.value }))}
                    className="text-xl font-bold bg-transparent border-none text-white focus:outline-none"
                  />
                  <span className="text-xs text-gray-500">{currentPipeline.steps.length} agents</span>
                </div>
                
                {/* Pipeline Canvas */}
                <div 
                  className="relative bg-gray-950 rounded-xl border border-gray-800 overflow-hidden"
                  style={{ height: '400px' }}
                >
                  {/* Grid background */}
                  <div 
                    className="absolute inset-0 opacity-20"
                    style={{
                      backgroundImage: 'radial-gradient(circle, #374151 1px, transparent 1px)',
                      backgroundSize: '20px 20px'
                    }}
                  />
                  
                  {/* Connection lines */}
                  {currentPipeline.steps.map((step, i) => {
                    if (i < currentPipeline.steps.length - 1) {
                      return (
                        <ConnectionLine
                          key={`line-${i}`}
                          from={step.position}
                          to={currentPipeline.steps[i + 1].position}
                          isActive={isExecuting && currentStep === i}
                        />
                      );
                    }
                    return null;
                  })}
                  
                  {/* Agent Nodes */}
                  {currentPipeline.steps.map((step, i) => (
                    <AgentNode
                      key={`node-${i}`}
                      agent={{ ...step, type: step.type }}
                      position={step.position}
                      isActive={isExecuting && currentStep === i}
                      isCompleted={isExecuting && currentStep > i}
                      onClick={() => console.log('Configure step:', step)}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            {/* Sidebar */}
            <div>
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-5 mb-4">
                <h3 className="font-semibold text-white mb-4">Pipeline Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Agents</span>
                    <span className="text-white">{currentPipeline.steps.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Est. Credits</span>
                    <span className="text-amber-400">~{selectedTemplate?.credits_estimate || 45}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Time Saved</span>
                    <span className="text-emerald-400">{selectedTemplate?.time_saved || '15+ hours'}</span>
                  </div>
                </div>
              </div>
              
              {/* Steps List */}
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-5">
                <h3 className="font-semibold text-white mb-4">Workflow Steps</h3>
                <div className="space-y-2">
                  {currentPipeline.steps.map((step, i) => (
                    <div 
                      key={i}
                      className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg"
                    >
                      <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold">
                        {i + 1}
                      </div>
                      <span className="text-white">{step.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Execution View */}
        {view === 'execution' && currentPipeline && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Visual Progress */}
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
              <h3 className="font-semibold text-white mb-4">Pipeline Execution</h3>
              
              <div 
                className="relative bg-gray-950 rounded-xl border border-gray-800 overflow-hidden"
                style={{ height: '300px' }}
              >
                {/* Grid background */}
                <div 
                  className="absolute inset-0 opacity-20"
                  style={{
                    backgroundImage: 'radial-gradient(circle, #374151 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }}
                />
                
                {/* Connection lines */}
                {currentPipeline.steps.map((step, i) => {
                  if (i < currentPipeline.steps.length - 1) {
                    return (
                      <ConnectionLine
                        key={`line-${i}`}
                        from={step.position || { x: 100 + (i * 200), y: 120 }}
                        to={currentPipeline.steps[i + 1].position || { x: 100 + ((i + 1) * 200), y: 120 }}
                        isActive={currentStep === i}
                      />
                    );
                  }
                  return null;
                })}
                
                {/* Agent Nodes */}
                {currentPipeline.steps.map((step, i) => (
                  <AgentNode
                    key={`node-${i}`}
                    agent={{ ...step, type: step.type }}
                    position={step.position || { x: 100 + (i * 200), y: 120 }}
                    isActive={currentStep === i}
                    isCompleted={currentStep === -1 || currentStep > i}
                  />
                ))}
              </div>
            </div>
            
            {/* Execution Progress Panel */}
            <ExecutionProgress
              pipeline={currentPipeline}
              currentStep={currentStep}
              logs={executionLogs}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PipelineBuilder;
